import type { ReactNode } from "react";
import { Badge } from "@/components/shadcn/badge";

type SignInMethodPanelProps = {
  /** Brand or method icon, shown in a tile to the left of the title. */
  icon: ReactNode;
  title: string;
  description: string;
  /** The method currently used to sign in. */
  isActive: boolean;
  /** True when the state could not be read, so neither in-use nor not-in-use is claimed. */
  isStateUnknown?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
};

/**
 * Shared shell for a sign-in method so password and Microsoft carry equal visual
 * weight, rather than one reading as a footnote to the other.
 */
export const SignInMethodPanel = ({
  icon,
  title,
  description,
  isActive,
  isStateUnknown = false,
  actions,
  children,
}: SignInMethodPanelProps) => (
  <div className="rounded-lg border bg-muted/30 p-4">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border bg-white">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold">{title}</p>
            {isStateUnknown
              ? <Badge variant="outline">Unknown</Badge>
              : isActive
                ? <Badge variant="success">In use</Badge>
                : <Badge variant="secondary">Not in use</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>

    {children && <div className="mt-4 border-t pt-4">{children}</div>}
  </div>
);
